// src/oura/components/site/StrengthsWeaknessesCard.tsx
//
// What the site already does well, to protect, beside the gaps holding it
// back, to fix. All of each, never a "Show more": there are at most five.

import type { ReactNode } from "react";
import { ScaleIcon } from "lucide-react";
import { cn } from "../../lib/cn";
import { Dot, FoldCard, TYPE, type Tone } from "@oura/ui";
import { splitItem } from "./siteCopy";

function Column({ title, titleClass, tone, items, empty }: { title: string; titleClass: string; tone: Tone; items: string[]; empty: string }) {
    return (
        <div className="min-w-0">
            <p className={cn(TYPE.tc, "m-0 font-semibold", titleClass)}>{title}</p>
            {items.length === 0 ? (
                <p className={cn(TYPE.tc, "m-0 mt-3 text-gray-500")}>{empty}</p>
            ) : (
                <ul className="list-plain m-0 mt-3 space-y-3 p-0">
                    {items.map((text) => {
                        const { title: head, line } = splitItem(text);
                        return (
                            <li key={text} className="flex gap-3">
                                <Dot tone={tone} className="mt-1.5" />
                                <div className="min-w-0">
                                    <p className={cn(TYPE.tb, "m-0 font-medium text-gray-900")}>{head}</p>
                                    {line && <p className={cn(TYPE.tc, "m-0 text-gray-500")}>{line}</p>}
                                </div>
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
}

export function StrengthsWeaknessesCard({
    strengths,
    weaknesses,
    unread,
    busy,
    refresh,
    progress,
}: {
    strengths: string[];
    weaknesses: string[];
    /**
     * The crawl could not read the site. The backend still lists every check
     * that found nothing as a strength then ("No broken pages"), which is a
     * claim about pages nobody read, so the strengths are not shown. The
     * weaknesses are: a check that did find something (every page a redirect
     * loop) is what explains why the crawl failed.
     */
    unread: boolean;
    busy: boolean;
    refresh?: ReactNode;
    progress?: ReactNode;
}) {
    const shownStrengths = unread ? [] : strengths;
    const s = shownStrengths.length;
    const w = weaknesses.length;
    const count = `${s} ${s === 1 ? "strength" : "strengths"}, ${w} ${w === 1 ? "weakness" : "weaknesses"}`;
    return (
        <FoldCard
            id="strengths-weaknesses"
            title="Strengths and weaknesses"
            icon={{ icon: ScaleIcon, area: "site" }}
            right={
                <>
                    {s + w > 0 && <span>{count}</span>}
                    {refresh}
                </>
            }
        >
            {progress && <div className="mb-3 space-y-1 empty:hidden">{progress}</div>}
            <div aria-busy={busy} className={cn("grid gap-6 transition-opacity md:grid-cols-2", busy && "opacity-50")}>
                <Column
                    title="Protect"
                    titleClass="text-emerald-700"
                    tone="green"
                    items={shownStrengths}
                    empty={unread ? "Nothing to protect until the crawl can read the site." : "The crawl read the site and found nothing to single out yet."}
                />
                <Column
                    title="Fix"
                    titleClass="text-rose-700"
                    tone="rose"
                    items={weaknesses}
                    empty={unread ? "The crawl could not read the site, and no check says why." : "The crawl read the site and flagged nothing on this scan."}
                />
            </div>
        </FoldCard>
    );
}
